import React, { useMemo } from "react";
import { Row, Col } from "reactstrap";
import MenuCard from "../MenuCard";
import { WORKFLOWS } from "../MenuHub";
import useAuth from "../../../pages/Personal Attendance/useAuth";
import { openExternalApp } from "../../../helpers/externalApps";
import { DEPARTMENTS, inDepartment, visibleReports } from "../../../pages/KPI/reportConfig";

/**
 * Owner (2026-10-05): the department pages show MODULES, one card per module, the same
 * cards as General SS White (the Workflows hub), not a shortcut per screen. "Put the DMS
 * module in Engineering, not Create ECN etc. separately." The reports are one module
 * too: a "Reports & KPI" card that opens the KPI hub on that department's tab.
 *
 * The cards come straight from the hub's catalog (MenuHub.WORKFLOWS) with the hub's own
 * rules: a module shows when its gate passes and, when it has sub-actions, when at least
 * one of them is visible; such a module opens its hub page (/workflows?wf=<key>) with the
 * same drill-down the person knows from General SS White, the others open directly. Apps
 * (Mafia Aux, Perflexion, Expected Visitors, Tool Tracker) are the Apps card's entries.
 */

// Modules that are not in the Workflows catalog.
const LOCAL_MODULES = {
    admin: {
        key: "admin", label: "Admin", subtitle: "Roles, permissions, EPO limits, departments",
        icon: "ri-shield-user-line", tone: "primary", to: "/admin", module: "admin.hub",
    },
};

// The hub's visibility rule (MenuHub.isVisible), with the IT Admin pass of the routes.
const isVisible = (auth, entry) => {
    if (!entry) return false;
    if (entry.module) {
        return !!auth?.isItAdmin
            || (typeof auth?.canAccessModule === "function" && auth.canAccessModule(entry.module));
    }
    if (entry.requiresManagerOrAdmin) return !!(auth?.isManager || auth?.isItAdmin || auth?.isHR || auth?.isSuperUser);
    return true;
};

const card = (entry, extra = {}) => ({
    key: entry.key, icon: entry.icon, label: entry.label, subtitle: entry.subtitle, tone: entry.tone, ...extra,
});

/** The "Reports & KPI" module: the hub on the department's tab, when the person may see a report there. */
const kpiCard = (auth, dept) => {
    const tab = dept && DEPARTMENTS.some((d) => d.key === dept) ? dept : "all";
    const mine = visibleReports(auth).filter((r) => inDepartment(r, tab));
    const mayOpenHub = !!auth?.isItAdmin
        || (typeof auth?.canAccessModule === "function" && auth.canAccessModule("dept.kpi"));
    if (mine.length === 0 && !mayOpenHub) return null;
    const label = DEPARTMENTS.find((d) => d.key === tab)?.label;
    return {
        key: "kpi",
        icon: "ri-bar-chart-grouped-line",
        label: "Reports & KPI",
        subtitle: tab === "all" ? "Power BI dashboards and management reports" : `${label} reports in the KPI hub`,
        tone: "primary",
        count: mine.length > 0 ? mine.length : undefined,
        to: tab === "all" ? "/kpi" : `/kpi?tab=${tab}`,
    };
};

const resolve = (auth, id, dept) => {
    if (id === "kpi") return kpiCard(auth, dept);

    if (LOCAL_MODULES[id]) {
        const m = LOCAL_MODULES[id];
        return isVisible(auth, m) ? card(m, { to: m.to }) : null;
    }

    const wf = WORKFLOWS.find((w) => w.key === id);
    if (wf) {
        if (!isVisible(auth, wf)) return null;
        const subs = (wf.subActions || []).filter((s) => !s.divider && isVisible(auth, s));
        if ((wf.subActions || []).length > 0 && subs.length === 0) return null;
        return subs.length > 0
            ? card(wf, { count: subs.length, to: `/workflows?wf=${wf.key}` })
            : card(wf, { to: wf.to });
    }

    const apps = WORKFLOWS.find((w) => w.key === "apps");
    const app = (apps?.subActions || []).find((s) => s.key === id);
    if (app) {
        if (!isVisible(auth, app)) return null;
        return app.external ? card(app, { external: app.to }) : card(app, { to: app.to });
    }

    if (process.env.NODE_ENV !== "production") console.warn(`DeptModulesGrid: unknown module "${id}"`);
    return null;
};

/**
 * ids: module keys of the Workflows catalog ("dms", "tasks", "epo", "cr", "project", "pac",
 * "travel", "time", "orgchart", "di", "display", "support", "training"), an Apps entry
 * ("app-mafia", "app-perflexion", "app-visitors", "app-tooltracker"), "admin" or "kpi".
 * dept: the KPI hub tab the "kpi" card opens (DEPARTMENTS key).
 */
// Owner (2026-10-06): no note under the heading (it named "General SS White").
export function DeptModulesGrid({ ids, dept, title = "Modules", description = "", icon = "ri-apps-2-line" }) {
    const auth = useAuth();
    const cards = useMemo(
        () => (ids || []).map((id) => resolve(auth, id, dept)).filter(Boolean),
        [ids, dept, auth]
    );
    if (cards.length === 0) return null;
    return (
        <div className="mb-4">
            <div className="d-flex align-items-center gap-2 mb-2 px-1 flex-wrap">
                <i className={icon} style={{ fontSize: 16, color: "var(--ssw-ink-blue, #405189)" }} aria-hidden="true" />
                <h6 className="fw-semibold mb-0" style={{ color: "var(--ssw-fg-1, #212529)" }}>{title}</h6>
                {description ? (
                    <span className="text-muted small" style={{ fontSize: 12, marginLeft: 8 }}>{description}</span>
                ) : null}
            </div>
            <Row className="g-3">
                {cards.map((c) => (
                    <Col xxl={3} xl={4} md={6} sm={6} key={c.key}>
                        <div style={{ height: "100%" }}>
                            <MenuCard
                                icon={c.icon}
                                label={c.label}
                                subtitle={c.subtitle}
                                tone={c.tone}
                                count={c.count}
                                to={c.to}
                                onClick={c.external ? () => openExternalApp(c.external) : undefined}
                            />
                        </div>
                    </Col>
                ))}
            </Row>
        </div>
    );
}
